import type { OrgType } from '../tenants/index';

/**
 * De woorden van een type organisatie (ORG-2, ADR-0087).
 *
 * Alle teksten van de app zijn geschreven voor een productiebedrijf: "medewerker", "bedrijf",
 * "personeelsnummer", "werkgever". Een vereniging heeft leden, geen medewerkers. In plaats van
 * honderden teksten te herschrijven met variabelen, zet deze laag een afgewerkte tekst om met een
 * tabel per taal en type. Ze werkt daardoor ook voor teksten die later bijkomen, en voor de mails
 * van de server: web en API gebruiken dezelfde functie.
 *
 * **De volgorde in een tabel telt.** Een woordgroep met lidwoord staat vóór het losse woord, want
 * het lidwoord verandert mee: "de medewerker" wordt "het lid", "dit bedrijf" wordt "deze
 * vereniging". Bij gelijke lengte wint wat eerst staat; de regex probeert de langste eerst.
 *
 * **Hoofdletters.** Begint het gevonden woord met een hoofdletter, dan ook de vervanging.
 *
 * **Samenstellingen.** Een regel met `prefix` vervangt ook het begin van een samengesteld woord
 * ("bedrijfscode" wordt "verenigingscode"); een gewone regel alleen een heel woord, zodat
 * "ploegoverdracht" geen "teamoverdracht" wordt.
 */

type Rule = readonly [from: string, to: string, prefix?: 'prefix'];
type Language = 'nl' | 'fr' | 'en';

const NL_ASSOCIATION: readonly Rule[] = [
  ['elk bedrijf', 'elke vereniging'],
  ['ieder bedrijf', 'iedere vereniging'],
  // Met lidwoord of aanwijzend woord: het geslacht wisselt (de medewerker / het lid).
  ['het hele bedrijf', 'de hele vereniging'],
  ['het bedrijf', 'de vereniging'],
  ['dit bedrijf', 'deze vereniging'],
  ['dat bedrijf', 'die vereniging'],
  ['een nieuw bedrijf', 'een nieuwe vereniging'],
  ['een leeg bedrijf', 'een lege vereniging'],
  ['een vers bedrijf', 'een verse vereniging'],
  ['een ander bedrijf', 'een andere vereniging'],
  ['ander bedrijf', 'andere vereniging'],
  ['de medewerkers', 'de leden'],
  // Een bijvoeglijk naamwoord verbuigt mee met het lidwoord: "de nieuwe medewerker" → "het nieuwe lid".
  ['de nieuwe medewerker', 'het nieuwe lid'],
  ['een nieuwe medewerker', 'een nieuw lid'],
  ['de medewerker', 'het lid'],
  ['deze medewerker', 'dit lid'],
  ['die medewerker', 'dat lid'],
  ['elke medewerker', 'elk lid'],
  ['iedere medewerker', 'ieder lid'],
  ['de werkgever', 'de vereniging'],
  ['je werkgever', 'je vereniging'],
  ['jouw werkgever', 'jouw vereniging'],
  ['uw werkgever', 'uw vereniging'],
  ['de leidinggevende', 'de verantwoordelijke'],
  // Een vereniging heeft geen ondernemingsraad; wie meebeslist, is het bestuur.
  ['je ondernemingsraad', 'het bestuur'],
  ['de ondernemingsraad', 'het bestuur'],
  ['ondernemingsraad', 'bestuur'],
  // Losse woorden en samenstellingen.
  ['bedrijven', 'verenigingen'],
  ['bedrijfs', 'verenigings', 'prefix'],
  ['bedrijf', 'vereniging'],
  ['medewerkers', 'leden', 'prefix'],
  ['medewerker', 'lid'],
  ['personeelsnummers', 'lidnummers'],
  ['personeelsnummer', 'lidnummer'],
  ['werkgevers', 'verenigingen'],
  ['werkgever', 'vereniging'],
  ['ploegen', 'teams'],
  ['ploeg', 'team'],
  ['leidinggevenden', 'verantwoordelijken'],
  ['leidinggevende', 'verantwoordelijke'],
  ['collega’s', 'medeleden'],
  ["collega's", 'medeleden'],
  ['collega', 'medelid'],
];

const EN_ASSOCIATION: readonly Rule[] = [
  ['your works council', 'the board'],
  ['works council', 'board'],
  ['employee numbers', 'member numbers'],
  ['employee number', 'member number'],
  ['employees', 'members'],
  ['employee', 'member'],
  ['employers', 'associations'],
  ['employer', 'association'],
  ['companies', 'associations'],
  ['company', 'association'],
  ['colleagues', 'fellow members'],
  ['a colleague', 'a fellow member'],
  ['colleague', 'fellow member'],
  ['managers', 'coordinators'],
  ['manager', 'coordinator'],
];

const FR_ASSOCIATION: readonly Rule[] = [
  ['conseil d’entreprise', 'conseil d’administration'],
  ["conseil d'entreprise", "conseil d'administration"],
  // "l’entreprise" en "l’association" hebben hetzelfde lidwoord; "cette entreprise" ook.
  ['numéros de personnel', 'numéros de membre'],
  ['numéro de personnel', 'numéro de membre'],
  ['matricules', 'numéros de membre'],
  ['le matricule', 'le numéro de membre'],
  ['un matricule', 'un numéro de membre'],
  ['matricule', 'numéro de membre'],
  ['les collaborateurs', 'les membres'],
  ['le collaborateur', 'le membre'],
  ['un collaborateur', 'un membre'],
  ['collaboratrices', 'membres'],
  ['collaboratrice', 'membre'],
  ['collaborateurs', 'membres'],
  ['collaborateur', 'membre'],
  ["l'employé", 'le membre'],
  ['l’employé', 'le membre'],
  ['employés', 'membres'],
  ['employé', 'membre'],
  ['employeurs', 'associations'],
  ['employeur', 'association'],
  ['entreprises', 'associations'],
  ['entreprise', 'association'],
  ['un collègue', 'un autre membre'],
  ['collègues', 'autres membres'],
  ['collègue', 'autre membre'],
];

/**
 * Een sportclub (ORG-3): leden, de club, de trainer en het bestuur.
 *
 * Bewust anders dan de algemene vereniging. "Club" is een de-woord, zoals "vereniging", dus de
 * lidwoorden lopen gelijk ("dit bedrijf" → "deze club"). Maar de ploeg blijft een ploeg: in een
 * Vlaamse sportclub is dat het gewone woord voor een team, en "ploegoverdracht" staat bij een club
 * toch uit. Wie in een fabriek "leidinggevende" is, is in een club de trainer; een collega is een
 * clubgenoot.
 */
const NL_SPORTS_CLUB: readonly Rule[] = [
  ['elk bedrijf', 'elke club'],
  ['ieder bedrijf', 'iedere club'],
  ['het hele bedrijf', 'de hele club'],
  ['het bedrijf', 'de club'],
  ['dit bedrijf', 'deze club'],
  ['dat bedrijf', 'die club'],
  ['een nieuw bedrijf', 'een nieuwe club'],
  ['een leeg bedrijf', 'een lege club'],
  ['een vers bedrijf', 'een verse club'],
  ['een ander bedrijf', 'een andere club'],
  ['ander bedrijf', 'andere club'],
  ['de medewerkers', 'de leden'],
  // Een bijvoeglijk naamwoord verbuigt mee met het lidwoord: "de nieuwe medewerker" → "het nieuwe lid".
  ['de nieuwe medewerker', 'het nieuwe lid'],
  ['een nieuwe medewerker', 'een nieuw lid'],
  ['de medewerker', 'het lid'],
  ['deze medewerker', 'dit lid'],
  ['die medewerker', 'dat lid'],
  ['elke medewerker', 'elk lid'],
  ['iedere medewerker', 'ieder lid'],
  ['de werkgever', 'de club'],
  ['je werkgever', 'je club'],
  ['jouw werkgever', 'jouw club'],
  ['uw werkgever', 'uw club'],
  // Het bestuur beslist mee, zoals een ondernemingsraad; het is een het-woord.
  ['je ondernemingsraad', 'het bestuur'],
  ['de ondernemingsraad', 'het bestuur'],
  ['ondernemingsraad', 'bestuur'],
  // Losse woorden en samenstellingen: "bedrijfscode" wordt "clubcode".
  ['bedrijven', 'clubs'],
  ['bedrijfs', 'club', 'prefix'],
  ['bedrijf', 'club'],
  ['medewerkers', 'leden', 'prefix'],
  ['medewerker', 'lid'],
  ['personeelsnummers', 'lidnummers'],
  ['personeelsnummer', 'lidnummer'],
  ['werkgevers', 'clubs'],
  ['werkgever', 'club'],
  ['leidinggevenden', 'trainers'],
  ['leidinggevende', 'trainer'],
  ['collega’s', 'clubgenoten'],
  ["collega's", 'clubgenoten'],
  ['collega', 'clubgenoot'],
  // HR heet in een club het bestuur (zoals de rol). Alleen in vaste woordgroepen: "HR" los vervangen
  // zou midden in een zin een hoofdletter geven ("vraag Het bestuur").
  ['je ploegleider of hr', 'je trainer of het bestuur'],
  ['vraag hr', 'vraag het bestuur'],
  ['contact op met hr', 'contact op met het bestuur'],
  ['hr bevestigt', 'het bestuur bevestigt'],
];

const EN_SPORTS_CLUB: readonly Rule[] = [
  ['your works council', 'the board'],
  ['works council', 'board'],
  ['employee numbers', 'member numbers'],
  ['employee number', 'member number'],
  ['employees', 'members'],
  ['employee', 'member'],
  ['employers', 'clubs'],
  ['employer', 'club'],
  ['companies', 'clubs'],
  ['company', 'club'],
  ['colleagues', 'teammates'],
  ['colleague', 'teammate'],
  ['managers', 'coaches'],
  ['manager', 'coach'],
  ['your team lead or hr', 'your coach or the board'],
  ['ask hr', 'ask the board'],
  ['contact hr', 'contact the board'],
  ['hr first confirms', 'the board first confirms'],
];

/**
 * Frans: "le club" is mannelijk en begint met een medeklinker, dus het lidwoord vóór "entreprise"
 * verandert mee: "l’entreprise" → "le club", "de l’entreprise" → "du club", "cette entreprise" →
 * "ce club". De woordgroepen staan daarom bovenaan.
 */
const FR_SPORTS_CLUB: readonly Rule[] = [
  ['conseil d’entreprise', 'comité'],
  ["conseil d'entreprise", 'comité'],
  ['de l’entreprise', 'du club'],
  ["de l'entreprise", 'du club'],
  ['à l’entreprise', 'au club'],
  ["à l'entreprise", 'au club'],
  ['l’entreprise', 'le club'],
  ["l'entreprise", 'le club'],
  ['de l’employeur', 'du club'],
  ["de l'employeur", 'du club'],
  ['l’employeur', 'le club'],
  ["l'employeur", 'le club'],
  // Een vrouwelijk woord wordt mannelijk: de bijvoeglijke naamwoorden in de teksten verbuigen mee.
  ['une autre entreprise', 'un autre club'],
  ['d’une entreprise à l’autre', 'd’un club à l’autre'],
  ["d'une entreprise à l'autre", "d'un club à l'autre"],
  ['une entreprise neuve et vide', 'un club neuf et vide'],
  ['une entreprise neuve', 'un club neuf'],
  ['une nouvelle entreprise', 'un nouveau club'],
  ['nouvelle entreprise créée', 'nouveau club créé'],
  ['aucune entreprise trouvée', 'aucun club trouvé'],
  ['aucune entreprise', 'aucun club'],
  ['la première entreprise', 'le premier club'],
  ['cette entreprise est suspendue', 'ce club est suspendu'],
  ['cette entreprise de démo personnelle', 'ce club de démo personnel'],
  ['toutes les entreprises', 'tous les clubs'],
  ['d’entreprises', 'de clubs'],
  ["d'entreprises", 'de clubs'],
  ['d’entreprise', 'de club'],
  ["d'entreprise", 'de club'],
  ['cette entreprise', 'ce club'],
  ['une entreprise', 'un club'],
  ['chaque entreprise', 'chaque club'],
  ['toute l’entreprise', 'tout le club'],
  ["toute l'entreprise", 'tout le club'],
  ['autre entreprise', 'autre club'],
  ['numéros de personnel', 'numéros de membre'],
  ['numéro de personnel', 'numéro de membre'],
  ['matricules', 'numéros de membre'],
  ['le matricule', 'le numéro de membre'],
  ['un matricule', 'un numéro de membre'],
  ['matricule', 'numéro de membre'],
  ['les collaborateurs', 'les membres'],
  ['le collaborateur', 'le membre'],
  ['un collaborateur', 'un membre'],
  ['collaboratrices', 'membres'],
  ['collaboratrice', 'membre'],
  ['collaborateurs', 'membres'],
  ['collaborateur', 'membre'],
  ["l'employé", 'le membre'],
  ['l’employé', 'le membre'],
  ['employés', 'membres'],
  ['employé', 'membre'],
  ['employeurs', 'clubs'],
  ['employeur', 'club'],
  ['entreprises', 'clubs'],
  ['entreprise', 'club'],
  ['un collègue', 'un coéquipier'],
  ['collègues', 'coéquipiers'],
  ['collègue', 'coéquipier'],
  ["à votre chef d'équipe ou aux rh", 'à votre entraîneur ou au comité'],
  ['aux rh', 'au comité'],
  ['contactez les rh', 'contactez le comité'],
  ['les rh confirment', 'le comité confirme'],
];

/** Een school: personeelsleden in plaats van medewerkers, een school in plaats van een bedrijf. */
const NL_SCHOOL: readonly Rule[] = [
  ['elk bedrijf', 'elke school'],
  ['ieder bedrijf', 'iedere school'],
  ['het hele bedrijf', 'de hele school'],
  ['het bedrijf', 'de school'],
  ['dit bedrijf', 'deze school'],
  ['dat bedrijf', 'die school'],
  ['een nieuw bedrijf', 'een nieuwe school'],
  ['een ander bedrijf', 'een andere school'],
  ['ander bedrijf', 'andere school'],
  ['de medewerkers', 'de personeelsleden'],
  ['de medewerker', 'het personeelslid'],
  ['deze medewerker', 'dit personeelslid'],
  ['die medewerker', 'dat personeelslid'],
  ['elke medewerker', 'elk personeelslid'],
  ['bedrijven', 'scholen'],
  ['bedrijfs', 'school', 'prefix'],
  ['bedrijf', 'school'],
  ['medewerkers', 'personeelsleden'],
  ['medewerker', 'personeelslid'],
  ['ploegen', 'teams'],
  ['ploeg', 'team'],
];

/** Overheid, zorg en kantoor: medewerkers blijven medewerkers, een bedrijf wordt een organisatie. */
const NL_ORGANISATION: readonly Rule[] = [
  ['elk bedrijf', 'elke organisatie'],
  ['ieder bedrijf', 'iedere organisatie'],
  ['het hele bedrijf', 'de hele organisatie'],
  ['het bedrijf', 'de organisatie'],
  ['dit bedrijf', 'deze organisatie'],
  ['dat bedrijf', 'die organisatie'],
  ['een nieuw bedrijf', 'een nieuwe organisatie'],
  ['een ander bedrijf', 'een andere organisatie'],
  ['ander bedrijf', 'andere organisatie'],
  ['bedrijven', 'organisaties'],
  ['bedrijfs', 'organisatie', 'prefix'],
  ['bedrijf', 'organisatie'],
];

const EN_ORGANISATION: readonly Rule[] = [
  ['companies', 'organisations'],
  ['company', 'organisation'],
];

const FR_ORGANISATION: readonly Rule[] = [
  ['entreprises', 'organisations'],
  ['entreprise', 'organisation'],
];

const TABLES: Partial<Record<OrgType, Partial<Record<Language, readonly Rule[]>>>> = {
  association: { nl: NL_ASSOCIATION, en: EN_ASSOCIATION, fr: FR_ASSOCIATION },
  sports_club: { nl: NL_SPORTS_CLUB, en: EN_SPORTS_CLUB, fr: FR_SPORTS_CLUB },
  school: { nl: NL_SCHOOL, en: EN_ORGANISATION, fr: FR_ORGANISATION },
  government: { nl: NL_ORGANISATION, en: EN_ORGANISATION, fr: FR_ORGANISATION },
  care: { nl: NL_ORGANISATION, en: EN_ORGANISATION, fr: FR_ORGANISATION },
  office: { nl: NL_ORGANISATION, en: EN_ORGANISATION, fr: FR_ORGANISATION },
  // Een fabriek: de teksten zijn voor haar geschreven, er valt niets te vervangen.
};

const LETTER = '\\p{L}';
const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

interface Compiled {
  pattern: RegExp;
  lookup: Map<string, string>;
}

const compiled = new Map<string, Compiled | null>();

function compile(orgType: OrgType, language: Language): Compiled | null {
  const key = `${orgType}:${language}`;
  if (compiled.has(key)) return compiled.get(key) ?? null;

  const rules = TABLES[orgType]?.[language];
  if (!rules || rules.length === 0) {
    compiled.set(key, null);
    return null;
  }

  // Langste eerst: "de medewerker" moet winnen van "medewerker".
  const ordered = [...rules].sort((a, b) => b[0].length - a[0].length);
  const lookup = new Map(ordered.map(([from, to]) => [from.toLowerCase(), to]));
  const pattern = new RegExp(
    `(?<!${LETTER})(?:${ordered
      // Een `prefix`-regel mag ook het begin van een langer woord zijn; een gewone alleen een heel woord.
      .map(([from, , prefix]) => (prefix ? escape(from) : `${escape(from)}(?!${LETTER})`))
      .join('|')})`,
    'giu',
  );
  const result = { pattern, lookup };
  compiled.set(key, result);
  return result;
}

/** Zet een afgewerkte tekst om naar de woorden van een type organisatie. */
export function applyTerminology(text: string, orgType: OrgType, language: string): string {
  // Een taal zonder tabel (Pools in de registratie, ORG-3) blijft zoals ze is. Vroeger viel ze
  // terug op de Nederlandse tabel; die vond in een Poolse zin niets, maar zou het wel doen bij een
  // Nederlands leenwoord.
  const base = language ? language.slice(0, 2) : 'nl';
  if (!(['nl', 'fr', 'en'] as const).includes(base as Language)) return text;
  const table = compile(orgType, base as Language);
  if (!table || !text) return text;

  return text.replace(table.pattern, (match) => {
    const replacement = table.lookup.get(match.toLowerCase()) ?? match;
    const first = match.charAt(0);
    return first !== first.toLowerCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  });
}

/** Heeft dit type eigen woorden? Een fabriek niet; dan hoeft er niets te gebeuren. */
export function hasTerminology(orgType: OrgType): boolean {
  return TABLES[orgType] !== undefined;
}
